// Building image-sequence sources from picked files, dropped folders or a folder handle (F-01.12).

import { groupSequences, isSequenceImage, type SequenceGroup } from '../core/sequence';
import { fingerprintFile } from '../core/project';
import type { SequenceInput } from './source-client';

type IterableDir = FileSystemDirectoryHandle & { values(): AsyncIterable<FileSystemHandle> };

/** Turns a group of numbered files into the worker input: files[i] is frame number first + i (null for gaps). */
export function sequenceFromGroup(g: SequenceGroup<{ name: string; file: File }>, fps: number): SequenceInput {
  const files: (File | null)[] = new Array(g.last - g.first + 1).fill(null);
  for (const f of g.frames) files[f.num - g.first] = f.item.file;
  return { kind: g.ext === '.exr' ? 'exr' : 'png', pattern: g.pattern, first: g.first, files, fps };
}

/** Image files among picked or dropped files, grouped into sequences (largest first). */
export function sequencesFromFiles(files: File[], fps: number): SequenceInput[] {
  return groupSequences(files.filter((f) => isSequenceImage(f.name)).map((file) => ({ name: file.name, file }))).map((g) => sequenceFromGroup(g, fps));
}

/** Image files directly inside a folder (not recursive). */
export async function imageFilesIn(dir: FileSystemDirectoryHandle): Promise<File[]> {
  const out: File[] = [];
  for await (const h of (dir as IterableDir).values()) {
    if (h.kind === 'file' && isSequenceImage(h.name)) out.push(await (h as FileSystemFileHandle).getFile());
  }
  return out;
}

/**
 * The sequence inside a folder: the one with the given pattern when known (re-linking), otherwise the largest.
 */
export async function sequenceFromDirectory(dir: FileSystemDirectoryHandle, fps: number, pattern?: string): Promise<SequenceInput | null> {
  const seqs = sequencesFromFiles(await imageFilesIn(dir), fps);
  return (pattern ? seqs.find((s) => s.pattern === pattern) : seqs[0]) ?? null;
}

/** File identity of a sequence: pattern name, total size, newest date, fingerprint over first/last file and count. */
export async function sequenceIdentity(s: SequenceInput): Promise<{ name: string; size: number; lastModified: number; fingerprint: string }> {
  const present = s.files.filter((f): f is File => !!f);
  const first = present[0];
  const last = present[present.length - 1];
  const chunk = 2 * 1024 * 1024;
  const blob = new Blob([first.slice(0, chunk), last.slice(0, chunk), `${s.pattern}|${s.first}|${s.files.length}|${present.length}`]);
  return {
    name: s.pattern,
    size: present.reduce((a, f) => a + f.size, 0),
    lastModified: present.reduce((a, f) => Math.max(a, f.lastModified), 0),
    fingerprint: await fingerprintFile(blob),
  };
}
